import React, { useState } from "react";
import { Twitter, Linkedin, Facebook, Mail, Link2, Check, MessageCircle, MessageSquare } from "lucide-react";

/**
 * ShareButtons — social-sharing bar for blog / writing posts.
 * Copy-safe: everything runs client-side. No tracking. No SDK.
 *
 * Renders X (Twitter), Facebook, LinkedIn, WhatsApp, Reddit, Email + Copy Link.
 * All targets open in a new tab; Copy uses the Clipboard API with a graceful
 * document.execCommand fallback for older browsers.
 */
export default function ShareButtons({ title = "", url = "", summary = "", variant = "inline", className = "" }) {
  const [copied, setCopied] = useState(false);
  const shareUrl = typeof window !== "undefined" && !url ? window.location.href : url;
  const safeTitle = encodeURIComponent(title);
  const safeUrl = encodeURIComponent(shareUrl);
  const safeSummary = encodeURIComponent(summary || title);

  const targets = [
    { name: "X",         href: `https://twitter.com/intent/tweet?text=${safeTitle}&url=${safeUrl}`,             Icon: Twitter,       color: "hover:bg-black hover:text-white hover:border-black" },
    { name: "Facebook",  href: `https://www.facebook.com/sharer/sharer.php?u=${safeUrl}`,                       Icon: Facebook,      color: "hover:bg-[#1877F2] hover:text-white hover:border-[#1877F2]" },
    { name: "LinkedIn",  href: `https://www.linkedin.com/sharing/share-offsite/?url=${safeUrl}`,                Icon: Linkedin,      color: "hover:bg-[#0A66C2] hover:text-white hover:border-[#0A66C2]" },
    { name: "WhatsApp",  href: `https://api.whatsapp.com/send?text=${safeTitle}%20${safeUrl}`,                  Icon: MessageCircle, color: "hover:bg-[#25D366] hover:text-white hover:border-[#25D366]" },
    { name: "Reddit",    href: `https://www.reddit.com/submit?url=${safeUrl}&title=${safeTitle}`,               Icon: MessageSquare, color: "hover:bg-[#FF4500] hover:text-white hover:border-[#FF4500]" },
    { name: "Email",     href: `mailto:?subject=${safeTitle}&body=${safeSummary}%0A%0A${safeUrl}`,              Icon: Mail,          color: "hover:bg-slate-800 hover:text-white hover:border-slate-800" },
  ];

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
    } catch {
      // Fallback for iframes / older browsers that block the Clipboard API.
      const t = document.createElement("textarea");
      t.value = shareUrl; document.body.appendChild(t); t.select();
      try { document.execCommand("copy"); } catch { /* ignore */ }
      document.body.removeChild(t);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  if (variant === "vertical") {
    return (
      <div data-testid="share-buttons-vertical" className={`flex flex-col gap-2 ${className}`}>
        {targets.map(({ name, href, Icon, color }) => (
          <a key={name} href={href} target="_blank" rel="noreferrer noopener"
             aria-label={`Share on ${name}`} data-testid={`share-${name.toLowerCase()}`}
             className={`w-10 h-10 grid place-items-center border border-border bg-background transition-colors ${color}`}>
            <Icon className="w-4 h-4" />
          </a>
        ))}
        <button onClick={copyLink} aria-label="Copy link" data-testid="share-copy"
                className={`w-10 h-10 grid place-items-center border border-border bg-background transition-colors ${copied ? "bg-primary text-white border-primary" : "hover:bg-primary hover:text-white hover:border-primary"}`}>
          {copied ? <Check className="w-4 h-4" /> : <Link2 className="w-4 h-4" />}
        </button>
      </div>
    );
  }

  return (
    <div data-testid="share-buttons" className={`flex flex-wrap items-center gap-2 ${className}`}>
      <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mr-1">Share</span>
      {targets.map(({ name, href, Icon, color }) => (
        <a key={name} href={href} target="_blank" rel="noreferrer noopener"
           aria-label={`Share on ${name}`} data-testid={`share-${name.toLowerCase()}`}
           className={`inline-flex items-center gap-2 border border-border px-3 py-2 text-xs font-medium transition-colors ${color}`}>
          <Icon className="w-3.5 h-3.5" />
          <span className="hidden sm:inline">{name}</span>
        </a>
      ))}
      <button onClick={copyLink} aria-label="Copy link" data-testid="share-copy"
              className={`inline-flex items-center gap-2 border px-3 py-2 text-xs font-medium transition-colors ${copied ? "bg-primary text-white border-primary" : "border-border hover:bg-primary hover:text-white hover:border-primary"}`}>
        {copied ? <Check className="w-3.5 h-3.5" /> : <Link2 className="w-3.5 h-3.5" />}
        <span className="hidden sm:inline">{copied ? "Copied!" : "Copy link"}</span>
      </button>
    </div>
  );
}
